"use client";

import { useEffect, useRef, useState } from "react";
import { Sparkles, Copy, X } from "lucide-react";

/**
 * Compact "Use with your agent" button. Click → small popover with copy-paste
 * prompt examples that teach the user how to delegate this section to their
 * Pauta MCP agent. Lives in the header of Docs / Issues views and inside the
 * Members modal.
 */

type Kind = "docs" | "issues" | "agents" | "feedback";

const PROMPTS: Record<
  Kind,
  { title: string; intro: string; examples: { ask: string; tool?: string }[]; tools: string }
> = {
  docs: {
    title: "Manage docs with your agent",
    intro:
      "Tell your Pauta agent (Claude Code with the pauta MCP) to read, create, or update docs. The agent already has the operating manual baked in — these are just examples of what to ask.",
    examples: [
      {
        ask: "Create a doc under the **Operations** section called **DB backup runbook** with the gpg + GCS upload steps from our last incident. Tag it #ops #runbook.",
        tool: "create_doc",
      },
      {
        ask: "Find every doc tagged #adr and give me a one-line summary of each.",
        tool: "list_docs / search_workspace",
      },
      {
        ask: "Open [[DB backup runbook]] and update the **Verification** section with the new pooler URL.",
        tool: "get_doc + update_doc",
      },
      {
        ask: "Reorganize: move the **API auth** doc under **Architecture** as a sub-doc of **Auth**.",
        tool: "update_doc (parent_id)",
      },
      {
        ask: "What are all the docs that link to [[DB backup runbook]]? (backlinks)",
        tool: "search_workspace",
      },
    ],
    tools: "list_docs · create_doc · update_doc · get_doc · search_workspace",
  },
  issues: {
    title: "Manage tasks with your agent",
    intro:
      "Your agent can create, triage, comment on, and move tasks — including referencing them as @ABC-123 in chat for the agent to resolve.",
    examples: [
      {
        ask: "Create a high-priority task in **Pauta** for fixing the IPv6 pg_dump cron failure. Assign to me. Add the failing run URL to the description.",
        tool: "create_issue",
      },
      {
        ask: "List every task assigned to me with status **in progress**.",
        tool: "list_issues",
      },
      {
        ask: "Move @PAUT-7 to **done** and post a comment with the deploy commit hash.",
        tool: "update_issue + add_comment",
      },
      {
        ask: "Show me all tasks in the @project:Q2-launch project that have no assignee.",
        tool: "list_issues",
      },
      {
        ask: "Summarize the last 7 days of activity across all my tasks, grouped by status change vs new.",
        tool: "list_activity",
      },
    ],
    tools: "list_issues · create_issue · update_issue · add_comment · list_activity",
  },
  agents: {
    title: "Brief your agent on this workspace",
    intro:
      "After creating an agent integration above, paste the briefing into the agent's Claude Code session. Then in everyday work, ask the agent things like:",
    examples: [
      {
        ask: "Send `Bootstrap.` as your first message — that auto-loads the workspace operating manual and mandatory protocol via `get_workspace_guide` and `whoami`. Then summarize back to me: who are you, what's this workspace, what's in your inbox?",
        tool: "Bootstrap → get_workspace_guide → whoami",
      },
      {
        ask: "What sessions did you save last week? List the open questions you flagged.",
        tool: "get_last_session / list saved sessions",
      },
      {
        ask: "Before we start: search for any existing docs or tasks on **Stripe webhook idempotency** so we don't duplicate.",
        tool: "search_workspace",
      },
      {
        ask: "End-of-session: save what we learned + open questions. Title it 'Stripe webhook hardening — initial pass'.",
        tool: "save_session",
      },
    ],
    tools: "get_workspace_guide · lookup_workspace · search_workspace · save_session · get_last_session",
  },
  feedback: {
    title: "Cross-workspace feedback (Triage)",
    intro:
      "Consumer-side agents push feedback into a linked dev workspace; dev-side agents triage it.",
    examples: [
      {
        ask: "**Consumer side:** A user just complained that the new chart doesn't load on mobile Safari. Build a doc under **Feedback / 2026-Q2** with their quote, repro steps, and your analysis. Then submit_feedback to **pauta-dev** with severity high, source_doc_id pointing at the doc.",
        tool: "create_doc + submit_feedback",
      },
      {
        ask: "**Dev side:** Show me the triage inbox, then for each new item give me a recommendation: convert to task, link to existing, or wontfix.",
        tool: "list_triage_inbox + triage_feedback",
      },
      {
        ask: "**Dev side:** Triage feedback <id> by converting it to a task in the **Pauta** team, priority high, assign to me.",
        tool: "triage_feedback",
      },
    ],
    tools: "submit_feedback · list_my_feedback · list_triage_inbox · triage_feedback",
  },
};

export function AgentHelpButton({
  kind,
  className,
  size = "sm",
}: {
  kind: Kind;
  className?: string;
  size?: "sm" | "md";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const cfg = PROMPTS[kind];

  return (
    <div ref={ref} className={`relative ${className ?? ""}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={
          "inline-flex items-center gap-1 rounded-md border border-[var(--border)] hover:border-[var(--accent)] hover:bg-[var(--accent-bg)]/30 transition-colors text-[var(--fg-secondary)] " +
          (size === "sm"
            ? "px-2 py-1 text-[11px]"
            : "px-2.5 py-1.5 text-[12px]")
        }
        title="See example prompts to use with your agent"
      >
        <Sparkles size={size === "sm" ? 11 : 12} className="text-[var(--accent)] shrink-0" />
        <span className="hidden sm:inline">Use with your agent</span>
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+6px)] w-[460px] max-w-[90vw] z-40 rounded-lg border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-2xl">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--border)]">
            <Sparkles size={13} className="text-[var(--accent)]" />
            <h4 className="text-[12.5px] font-semibold flex-1">{cfg.title}</h4>
            <button
              onClick={() => setOpen(false)}
              className="w-6 h-6 rounded-md hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)]"
            >
              <X size={11} />
            </button>
          </div>
          <div className="px-4 py-3 max-h-[60vh] overflow-y-auto scrollbar-thin">
            <p className="text-[11.5px] text-[var(--fg-tertiary)] leading-relaxed mb-3">
              {cfg.intro}
            </p>
            <div className="space-y-2">
              {cfg.examples.map((ex, i) => (
                <div
                  key={i}
                  className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-2.5 group hover:border-[var(--border-strong)] transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <div
                      className="flex-1 text-[12px] text-[var(--fg)] leading-snug"
                      // ex.ask is hand-authored, contains markdown-style bold via **; render as plain text
                    >
                      {ex.ask}
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(ex.ask)}
                      className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] transition-opacity"
                      title="Copy prompt"
                    >
                      <Copy size={10} />
                    </button>
                  </div>
                  {ex.tool && (
                    <div className="mt-1 text-[10px] font-mono text-[var(--fg-muted)]">
                      → {ex.tool}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="px-4 py-2 border-t border-[var(--border)] bg-[var(--bg-secondary)] text-[10.5px] text-[var(--fg-muted)] font-mono">
            MCP tools: {cfg.tools}
          </div>
        </div>
      )}
    </div>
  );
}
